import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiPath from '../../utils/apiPath';

const extractError = (err, rejectWithValue) => {
  if (err.response?.data) {
    const data = err.response.data;
    
    return rejectWithValue(data.message || data.error || data);
  }
  return rejectWithValue(err.message || "Something went wrong");
};

export const fetchWarehouses = createAsyncThunk(
  'warehouse/fetchAll',
  async ({ page = 1, limit = 10, search = '', field = '', is_active = '', date_range = '' }, { rejectWithValue }) => {
    try {
      let url = `/api/v1.0/warehouse?page=${page}&limit=${limit}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (field)  url += `&field=${encodeURIComponent(field)}`;
      if (is_active !== '') url += `&is_active=${is_active}`;
      if (date_range) url += `&date_range=${date_range}`;
      const response = await apiPath.get(url);
      return response.data;
    } catch (error) {
      return extractError(error, rejectWithValue);
    }
  }
);

export const fetchWarehouseLists = createAsyncThunk(
  'warehouse/fetchLists',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiPath.get('/api/v1.0/warehouse/lists');
      return response.data;
    } catch (error) {
      return extractError(error, rejectWithValue);
    }
  }
);

export const fetchWarehouseById = createAsyncThunk(
  'warehouse/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiPath.get(`/api/v1.0/warehouse/${id}`);
      return response.data;
    } catch (error) {
      return extractError(error, rejectWithValue);
    }
  }
);

export const createWarehouse = createAsyncThunk(
  'warehouse/create',
  async (warehouseData, { rejectWithValue }) => {
    try {
      const response = await apiPath.post('/api/v1.0/warehouse/create', warehouseData);
      return response.data;
    } catch (error) {
      return extractError(error, rejectWithValue);
    }
  }
);

export const updateWarehouse = createAsyncThunk(
  'warehouse/update',
  async ({ id, warehouseData }, { rejectWithValue }) => {
    try {
      const response = await apiPath.put(`/api/v1.0/warehouse/${id}`, warehouseData);
      return response.data;
    } catch (error) {
      return extractError(error, rejectWithValue);
    }
  }
);

export const deleteWarehouse = createAsyncThunk(
  'warehouse/delete',
  async (id, { rejectWithValue }) => {
    try {
      await apiPath.delete(`/api/v1.0/warehouse/${id}`);
      return id;
    } catch (error) {
      return extractError(error, rejectWithValue);
    }
  }
);

const warehouseSlice = createSlice({
  name: 'warehouse',
  initialState: {
    warehouses: [],
    warehouseLists: [],
    currentWarehouse: null,
    loading: false,
    error: null,
    totalRecord: 0,
    totalPage: 0,
    currentPage: 1,
    perPage: 10,
  },
  reducers: {
    clearWarehouseError: (state) => {
      state.error = null;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWarehouses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWarehouses.fulfilled, (state, action) => {
        state.loading = false;
        state.warehouses = action.payload.warehouses || [];
        state.totalRecord = action.payload.totalRecord || 0;
        state.totalPage = action.payload.totalPage || 0;
        state.currentPage = action.payload.currentPage || 1;
        state.perPage = action.payload.perPage || 10;
      })
      .addCase(fetchWarehouses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchWarehouseLists.pending, (state) => { state.loading = true; })
      .addCase(fetchWarehouseLists.fulfilled, (state, action) => {
        state.loading = false;
        state.warehouseLists = action.payload.warehouses || [];
      })
      .addCase(fetchWarehouseLists.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchWarehouseById.pending, (state) => { state.loading = true; })
      .addCase(fetchWarehouseById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentWarehouse = action.payload;
      })
      .addCase(fetchWarehouseById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createWarehouse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createWarehouse.fulfilled, (state, action) => {
        state.loading = false;
        const newWarehouse = action.payload.warehouse || action.payload;
        state.warehouses.unshift(newWarehouse);
        state.totalRecord += 1;
      })
      .addCase(createWarehouse.rejected, (state) => {
    
        state.loading = false;
      })

      .addCase(updateWarehouse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateWarehouse.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload.warehouse || action.payload;
        const index = state.warehouses.findIndex(w => w.id === updated.id);
        if (index !== -1) state.warehouses[index] = updated;
      })
      .addCase(updateWarehouse.rejected, (state) => {
        state.loading = false;
      })

      .addCase(deleteWarehouse.pending, (state) => { state.loading = true; })
      .addCase(deleteWarehouse.fulfilled, (state, action) => {
        state.loading = false;
        state.warehouses = state.warehouses.filter(w => w.id !== action.payload);
        state.totalRecord = Math.max(0, state.totalRecord - 1);
      })
      .addCase(deleteWarehouse.rejected, (state) => {
        
        state.loading = false;
      });
  },
});

export const { clearWarehouseError, setCurrentPage } = warehouseSlice.actions;
export default warehouseSlice.reducer;